import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import wait from '../../util/wait';
import {useWorkspaceDrawerContextMaybe} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {MediaBrowserPanelProps} from './Component';
import {fetchMaxStepWithAvailableMedia, useAugmentedData} from './fetch';
import {MediaBrowserPanel} from './MediaBrowserPanel';
import {getPaginatedTiles, getTileMediaSpecs} from './tiles';
import {useMediaPanelValues} from './useMediaPanelValues';
/**
 * This wrapper component is a hacky way of implementing a very specific behavior: When the user has auto-refresh enabled, we automatically increment the media panel step to the latest when new history data comes in with media paths. However, because history is updated faster than media becomes available, we end up just rendering a loading spinner while the new media finishes uploading.
 *
 * To fix this behavior, we pretend that the new steps which trickle in don't actually exist.
 *
 * Then, we prefetch the media from those steps. Only after the prefetch is successful (which confirms that at least some media from the new steps is available) do we tell the inner Media Panel component that those new history rows actually exist.
 */
const MediaBrowserPanelWrapperComp = (props: MediaBrowserPanelProps) => {
  const {data: unaugmentedData, query, config} = props;
  const data = useAugmentedData({data: unaugmentedData, query});
  const workspaceDrawerContext = useWorkspaceDrawerContextMaybe();

  /**
   * We're currently duplicating work done in the inner Media Panel component to calculate what media needs to be fetched given the history data returned. In the future, we may want to make all of these values less dependent on each other, but for now this should suffice.
   */
  const {mediaType, layout, tilePagination, stepMeta, snapToExistingStep} =
    useMediaPanelValues({
      ...props,
      data,
      /**
       *  We set the sliderKey to undefined to ensure we request the latest step. This is brittle as it relies on behavior in `useMediaPanelValues`.
       */
      config: useMemo(
        () => ({
          mode: 'gallery',
          mediaKeys: config.mediaKeys,
        }),
        [config]
      ),
    });
  const maxStepFromData = stepMeta.max;

  // State management for maxStep
  const [maxStep, setMaxStepStateOnly] = useState<number | null>(null);
  const maxStepRef = useRef(maxStep);
  const setMaxStep = useCallback((newMaxStep: number) => {
    if (maxStepRef.current != null && newMaxStep === 0) {
      return;
    }
    maxStepRef.current = newMaxStep;
    setMaxStepStateOnly(newMaxStep);
  }, []);

  // maxStep should never be greater than the data returned
  useEffect(() => {
    if (maxStep != null && maxStepFromData < maxStep) {
      setMaxStep(maxStepFromData);
    }
  }, [maxStep, setMaxStep, maxStepFromData]);

  const componentIsMountedRef = useRef(true);
  useEffect(
    () => () => {
      componentIsMountedRef.current = false;
    },
    []
  );

  const [reachedMaxFailedAttempts, setReachedMaxFailedAttempts] =
    useState(false);

  const numAttemptsToFetchMoreMedia = useRef(0);
  const maxAttemptsToFetchMoreMedia = 3;

  /**
   * When new history data comes in, prefetch the media specified by its paths and set `maxStep` to the latest step which has media actually available.
   */
  useEffect(() => {
    if (mediaType == null) {
      if (maxStep == null && !data.loading) {
        setMaxStep(maxStepFromData);
      }
      return;
    }

    if (maxStep != null && maxStepFromData <= maxStep) {
      return;
    }
    const paginatedTiles = getPaginatedTiles(
      layout.tiles,
      config.mode === 'grid' ? null : tilePagination
    );
    const tileMediaSpecs = getTileMediaSpecs(mediaType, paginatedTiles);
    const tileMediaSpecsAfterMaxStep = tileMediaSpecs.filter(
      s => maxStep == null || s.mediaStep > maxStep
    );
    if (tileMediaSpecsAfterMaxStep.length === 0) {
      return;
    }

    /**
     * Start a request loop that checks whether or not media is available for the latest step. The loop should terminate when media is available for the latest step or the component is unmounted.
     */
    (async () => {
      numAttemptsToFetchMoreMedia.current = 0;
      while (
        componentIsMountedRef.current &&
        (maxStepRef.current == null || maxStepRef.current < maxStepFromData)
      ) {
        numAttemptsToFetchMoreMedia.current += 1;

        const maxStepWithAvailableMedia = await fetchMaxStepWithAvailableMedia(
          tileMediaSpecsAfterMaxStep,
          !snapToExistingStep
        );

        // Stop if component has been unmounted
        if (!componentIsMountedRef.current) {
          return;
        }

        if (
          maxStepWithAvailableMedia != null &&
          (maxStepRef.current == null ||
            maxStepWithAvailableMedia > maxStepRef.current)
        ) {
          setMaxStep(maxStepWithAvailableMedia);

          // Stop if media is available for the latest step
          if (maxStepWithAvailableMedia >= maxStepFromData) {
            return;
          }
        }

        // Stop if we've attmpted 3 times and still haven't found any media
        if (
          numAttemptsToFetchMoreMedia.current === maxAttemptsToFetchMoreMedia
        ) {
          setReachedMaxFailedAttempts(true);
          return;
        }

        await wait(10000);
      }
    })();
  }, [
    config.mode,
    maxStep,
    setMaxStep,
    maxStepFromData,
    data.loading,
    mediaType,
    layout,
    tilePagination,
    snapToExistingStep,
  ]);

  // We pass this down to the inner Media Panel component, pretending that
  // the steps after `maxStep` do not exist yet.
  const dataCappedToMaxStep = useMemo(() => {
    // -----------------------------------------------------------------------
    // If we don't yet have a max step, we might still be polling for media
    // -----------------------------------------------------------------------
    if (maxStep == null) {
      // Show a spinner while polling
      if (
        !reachedMaxFailedAttempts &&
        !data.loading &&
        numAttemptsToFetchMoreMedia.current > 0
      ) {
        return {...data, loading: true};
      }

      // Ensure every run has at least an empty history array
      const updatedRuns = data.histories.data.map(run => {
        if (run.history === null || run.history === undefined) {
          return {...run, history: []};
        }
        return run;
      });

      // Only return a new object if something actually changed
      const hasChanges = updatedRuns.some(
        (run, idx) => run !== data.histories.data[idx]
      );
      if (!hasChanges) {
        return data;
      }

      return {
        ...data,
        histories: {
          ...data.histories,
          data: updatedRuns,
        },
      };
    }

    // -----------------------------------------------------------------------
    // We have a max step – cap each run's history to that step
    // -----------------------------------------------------------------------
    const cappedRuns = data.histories.data.map(run => {
      const originalHistory = run.history ?? [];
      const cappedHistory = originalHistory.filter(row => row._step <= maxStep);

      // Preserve reference when nothing changes
      if (cappedHistory.length === originalHistory.length) {
        return run;
      }

      return {...run, history: cappedHistory};
    });

    return {
      ...data,
      histories: {
        ...data.histories,
        data: cappedRuns,
      },
    };
  }, [data, maxStep, reachedMaxFailedAttempts]);

  // Hide the panel while the media settings drawer is opening and closing
  // because the `display: none` breaks the size calculation in `useMediaBrowserDimensions`
  if (workspaceDrawerContext?.shouldHidePanelContents) {
    return null;
  }

  return (
    <MediaBrowserPanel
      {...props}
      data={dataCappedToMaxStep}
      isMaxStepLoading={maxStep == null}
    />
  );
};

const MediaBrowserPanelWrapper = React.memo(MediaBrowserPanelWrapperComp);

export default MediaBrowserPanelWrapper;
